import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

import { DealersService } from "~/entity/Dealers/api/service";
import { IDealerListItem } from "~/entity/Dealers/model/schema";

export interface IDealerSelectOption {
  value: string;
  label: string;
}

const PAGE_SIZE = 25;

export interface IUseGetDealersForSelectConfig {
  pageSize?: number;
  immediate?: boolean;
}

export const toDealerSelectOption = (
  dealer: IDealerListItem,
): IDealerSelectOption => ({
  value: String(dealer.id),
  label: dealer.dealerName || dealer.name || String(dealer.id),
});

export const useGetDealersForSelect = (
  enabled = true,
  config?: IUseGetDealersForSelectConfig,
) => {
  const [options, setOptions] = useState<IDealerSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [activated, setActivated] = useState(false);

  const pageSize = config?.pageSize ?? PAGE_SIZE;
  const immediate = config?.immediate ?? false;

  const load = useCallback(
    async (pageNumber: number, dealerName: string, append: boolean) => {
      setLoading(true);
      try {
        const response = await DealersService.GET.getDealerList({
          page: pageNumber,
          count: pageSize,
          dealerSearch: dealerName ? { dealerName } : undefined,
          status: "active",
        });
        const { dealers, totalCount } = response.data;
        const mapped = dealers.map(toDealerSelectOption);

        setOptions((prev) => (append ? [...prev, ...mapped] : mapped));
        setPage(pageNumber);
        setHasMore((pageNumber + 1) * pageSize < totalCount);
      } catch (error) {
        console.error("Ошибка загрузки дилеров:", error);
      } finally {
        setLoading(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    if (!enabled) {
      setActivated(false);
      setOptions([]);
      setSearch("");
      setPage(0);
      setHasMore(false);
      return;
    }
    if (!activated && !immediate) {
      return;
    }
    load(0, search, false);
  }, [enabled, activated, immediate, search, load]);

  const onSearch = useMemo(
    () =>
      debounce((value: string) => {
        setActivated(true);
        setSearch(value);
      }, 300),
    [],
  );

  const onOpenChange = useCallback((open: boolean) => {
    if (open) {
      setActivated(true);
    }
  }, []);

  const onScrollBottom = useCallback(() => {
    if (hasMore && !loading) {
      load(page + 1, search, true);
    }
  }, [hasMore, loading, page, search, load]);

  return { options, loading, onSearch, onScrollBottom, onOpenChange };
};
